// The "Ad" label above each .fans sponsor card (Nicky, 13 Oct 2026): thin,
// small text in the page's own muted color, so it blends in (gray on black,
// off-white on a colored gradient) but always stays legible: an ad label has
// to be clear (FTC), so it must reach 4.5:1 against the background right
// behind it.
//
// .fans backgrounds are a linear gradient from gradient_start to gradient_end,
// and many fade from light to near-black, so one color per page can't work:
// the label is colored from the gradient color at its own position.

export type Rgb = [number, number, number];

export const AD_LABEL_MIN_CONTRAST = 4.5;

/** "#abc", "#aabbcc" or "rgb(1, 2, 3)" → [r, g, b]; anything else → null. */
export function parseColor(input: string | null | undefined): Rgb | null {
  const s = (input || '').trim();
  const rgb = s.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i);
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])].map((c) => Math.min(255, c)) as Rgb;
  let h = s.replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(h)) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

const channel = (c: number) => {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
};

/** WCAG relative luminance. */
export function luminance([r, g, b]: Rgb): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio, 1–21. */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** a → b by t (0–1), per channel, as CSS gradients interpolate (sRGB). */
export function mix(a: Rgb, b: Rgb, t: number): Rgb {
  const k = Math.max(0, Math.min(1, t));
  return [0, 1, 2].map((i) => Math.round(a[i] + (b[i] - a[i]) * k)) as Rgb;
}

/**
 * Where a point sits along a CSS linear-gradient (0 = start, 1 = end), for the
 * two directions .fans pages use: 180deg (top to bottom) and 135deg (top-left
 * to bottom-right). x/y are relative to the gradient box's top-left corner.
 */
export function gradientPosition(direction: string, x: number, y: number, width: number, height: number): number {
  if (width <= 0 || height <= 0) return 0;
  const t = direction === '135deg'
    ? 0.5 + ((x - width / 2) + (y - height / 2)) / (width + height)
    : y / height;
  return Math.max(0, Math.min(1, t));
}

/**
 * The label color for one spot on the page. It starts from the page's own
 * muted text (60% white on dark pages, a 60%-darkened tint on light ones,
 * matching lightTextSecondary) and only steps toward pure white or black, in
 * 5% steps, until it reaches 4.5:1. If the page's direction can't get there
 * on this background (a bright part of a "dark" page), it uses the other
 * direction; pure white or pure black always reaches at least 4.58:1.
 */
export function adLabelColor(bg: Rgb, pageIsLight: boolean): { color: Rgb; contrast: number } {
  const white: Rgb = [255, 255, 255];
  const black: Rgb = [0, 0, 0];
  const tryDirection = (target: Rgb) => {
    for (let strength = 0.6; strength <= 1.0001; strength += 0.05) {
      const color = mix(bg, target, strength);
      const contrast = contrastRatio(color, bg);
      if (contrast >= AD_LABEL_MIN_CONTRAST) return { color, contrast };
    }
    return null;
  };
  const preferred = pageIsLight ? black : white;
  const other = pageIsLight ? white : black;
  return tryDirection(preferred)
    ?? tryDirection(other)
    ?? (contrastRatio(white, bg) >= contrastRatio(black, bg)
      ? { color: white, contrast: contrastRatio(white, bg) }
      : { color: black, contrast: contrastRatio(black, bg) });
}

/**
 * What actually sits behind the "Ad" label now that it's inside the card's
 * top-right corner (Nicky, 27 Oct 2026): the card's own white wash over the
 * page gradient. The card is rgba(255,255,255,0.9) on light pages and
 * rgba(255,255,255,0.05) on dark ones (SponsorAdCard).
 */
export const AD_CARD_WASH = { light: 0.9, dark: 0.05 } as const;

export function cardBackdrop(pageColor: Rgb, pageIsLight: boolean): Rgb {
  return mix(pageColor, [255, 255, 255], pageIsLight ? AD_CARD_WASH.light : AD_CARD_WASH.dark);
}

export const rgbCss = ([r, g, b]: Rgb) => `rgb(${r}, ${g}, ${b})`;
